import React, { useState, useEffect, useContext } from "react";
import qs from "query-string";
import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import {
  Button,
  TextField,
  Typography,
  Container,
  Grid,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Popover,
  Menu,
  Hidden,
  Checkbox,
  FormControlLabel
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { IconButton, InputAdornment } from '@mui/material';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import MenuIcon from '@material-ui/icons/Menu';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { Helmet } from 'react-helmet';

import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import { openApi } from "../../services/api";
import toastError from "../../errors/toastError";
import ColorModeContext from "../../layout/themeContext";

// Reusing styles from Login page standard
const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4, 8),
    },
  },
  header: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoImg: {
    height: '40px',
    width: 'auto',
    objectFit: 'contain',
  },
  navMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  navLink: {
    color: '#1c1e21',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.95rem',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'none',
      opacity: 0.8
    }
  },
  mobileMenuBtn: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  mainContent: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
  },
  gridContainer: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftCol: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
      textAlign: 'center',
    },
  },
  rightCol: {
    display: 'flex',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(4),
      order: 2,
    },
  },
  heroTitle: {
    fontWeight: 800,
    fontSize: '3.5rem',
    lineHeight: 1.1,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  subText: {
    fontSize: '1.25rem',
    color: '#1c1e21',
    lineHeight: 1.5,
    marginBottom: theme.spacing(4),
    maxWidth: '500px',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
  },
  formContainer: {
    width: '100%',
    maxWidth: '400px',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  inputRounded: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '30px',
      backgroundColor: '#f5f6f7',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: '1px solid #ccc',
      },
      '&.Mui-focused fieldset': {
        border: `1px solid ${theme.palette.primary.main}`,
      },
      '& input': {
        padding: '16.5px 14px',
      },
      '& .MuiSelect-root': {
        padding: '16.5px 14px',
        textAlign: 'left'
      }
    },
  },
  submitButton: {
    borderRadius: '30px',
    padding: '12px',
    fontSize: '1rem',
    fontWeight: 'bold',
    textTransform: 'none',
    boxShadow: 'none',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: 'none',
    },
  },
  loginRow: {
    marginTop: theme.spacing(3),
    textAlign: 'center',
    fontSize: '0.95rem',
    color: '#1c1e21',
  },
  mockupImg: {
    width: '100%',
    maxWidth: '600px',
    height: 'auto',
    borderRadius: '20px',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '80%',
    }
  },
  footer: {
    marginTop: 'auto',
    paddingTop: theme.spacing(8),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    fontSize: '0.8rem',
    color: '#767676',
    justifyContent: 'center',
  },
  resourcesContent: {
    padding: theme.spacing(4),
    maxWidth: '900px',
    borderRadius: '16px',
  },
  resourceCard: {
    padding: theme.spacing(2),
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      cursor: 'pointer'
    }
  },
  resourceTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  resourceDesc: {
    color: '#65676b',
    fontSize: '0.9rem',
    marginBottom: theme.spacing(2),
    minHeight: '40px',
  },
  arrowIcon: {
    color: theme.palette.primary.main,
    fontSize: '1.2rem',
  }
}));

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muito curto!")
    .max(50, "Muito longo!")
    .required("Obrigatório"),
  companyName: Yup.string()
    .min(2, "Muito curto!")
    .max(50, "Muito longo!")
    .required("Obrigatório"),
  password: Yup.string().min(5, "Muito curto!").max(50, "Muito longo!"),
  email: Yup.string().email("Email inválido").required("Obrigatório"),
  phone: Yup.string().required("Obrigatório"),
  acceptTerms: Yup.bool().oneOf([true], "Você deve aceitar os termos"),
});

const SignUp = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { getPlanList } = usePlans();
  const { colorMode } = useContext(ColorModeContext);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [resourcesAnchorEl, setResourcesAnchorEl] = useState(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);

  let companyId = null;
  const params = qs.parse(window.location.search);
  if (params.companyId !== undefined) {
    companyId = params.companyId;
  }

  const initialState = {
    name: "",
    email: "",
    password: "",
    phone: "",
    companyId,
    companyName: "",
    planId: "",
    acceptTerms: false,
  };

  const [user] = useState(initialState);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://localhost:8090';

  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const url = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
        const response = await fetch(`${url}/settings/userCreation`);
        if (!response.ok) throw new Error('Status fetch failed');
        const data = await response.json();
        const isEnabled = data.userCreation === "enabled";
        setUserCreationEnabled(isEnabled);

        if (!isEnabled) {
          toast.info("Cadastro de novos usuários está desabilitado.");
          history.push("/login");
        }

      } catch (err) {
        console.error('Error userCreation:', err);
        // Em caso de erro, por segurança, redireciona ou avisa
        // mas aqui vamos manter a logica original de 'tenta' e se erro, default enabled=false e redirect
        setUserCreationEnabled(false);
        history.push("/login");
      }
    };
    fetchUserCreationStatus();
  }, [backendUrl, history]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      const planList = await getPlanList({ listPublic: "false" });
      setPlans(planList);
      setLoading(false);
    };
    fetchData();
  }, [getPlanList]);

  const handleSignUp = async (values) => {
    try {
      await openApi.post("/auth/signup", values);
      toast.success(i18n.t("signup.toasts.success"));
      history.push("/login");
    } catch (err) {
      toastError(err);
    }
  };

  const logoSrc = colorMode.appLogoLight || colorMode.appLogoFavicon;

  const handleResourcesClick = (event) => {
    setResourcesAnchorEl(event.currentTarget);
  };

  const handleResourcesClose = () => {
    setResourcesAnchorEl(null);
  };

  const openResources = Boolean(resourcesAnchorEl);

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };
  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const handleHelpClick = () => {
    const message = "Olá, gostaria de solicitar suporte técnico.";
    const phone = "5531983618392";
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (!userCreationEnabled) {
    return null;
  }

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Cadastre-se - ZapBot</title>
      </Helmet>

      <header className={classes.header}>
        <img src={logoSrc} alt='Logo' className={classes.logoImg} />

        <div className={classes.navMenu}>
          <div
            className={classes.navLink}
            onClick={handleResourcesClick}
            aria-owns={openResources ? 'resources-popover' : undefined}
            aria-haspopup="true"
          >
            Recursos <KeyboardArrowDownIcon fontSize="small" />
          </div>

          <div className={classes.navLink} onClick={handleHelpClick}>
            Central de Ajuda
          </div>
        </div>

        <Hidden mdUp>
          <IconButton
            className={classes.mobileMenuBtn}
            onClick={handleMobileMenuOpen}
          >
            <MenuIcon />
          </IconButton>
        </Hidden>
      </header>

      <Popover
        id="resources-popover"
        open={openResources}
        anchorEl={resourcesAnchorEl}
        onClose={handleResourcesClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          className: classes.resourcesContent,
          elevation: 4
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Box className={classes.resourceCard}>
              <Typography variant="h5" className={classes.resourceTitle}>Multiatendimento</Typography>
              <Typography className={classes.resourceDesc}>
                Conecte toda sua equipe em um único número de WhatsApp e organize seu fluxo.
              </Typography>
              <ArrowForwardIcon className={classes.arrowIcon} />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box className={classes.resourceCard}>
              <Typography variant="h5" className={classes.resourceTitle}>Automação</Typography>
              <Typography className={classes.resourceDesc}>
                Crie fluxos de conversa automáticos (chatbots) para atender 24h por dia.
              </Typography>
              <ArrowForwardIcon className={classes.arrowIcon} />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box className={classes.resourceCard}>
              <Typography variant="h5" className={classes.resourceTitle}>Gestão</Typography>
              <Typography className={classes.resourceDesc}>
                Relatórios detalhados, etiquetas e gestão completa do seu time em tempo real.
              </Typography>
              <ArrowForwardIcon className={classes.arrowIcon} />
            </Box>
          </Grid>
        </Grid>
      </Popover>

      <Menu
        anchorEl={mobileMenuAnchorEl}
        keepMounted
        open={Boolean(mobileMenuAnchorEl)}
        onClose={handleMobileMenuClose}
      >
        <MenuItem onClick={() => { handleResourcesClick({ currentTarget: mobileMenuAnchorEl }); handleMobileMenuClose(); }}>Recursos</MenuItem>
        <MenuItem onClick={() => { handleHelpClick(); handleMobileMenuClose(); }}>Central de Ajuda</MenuItem>
      </Menu>

      <Container maxWidth='lg' className={classes.mainContent}>
        <Grid container className={classes.gridContainer} spacing={4}>
          <Grid item xs={12} md={6} className={classes.leftCol}>
            <Typography variant='h1' className={classes.heroTitle}>
              Comece agora seu teste gratuito
            </Typography>

            <Typography variant='body1' className={classes.subText}>
              Crie sua conta em segundos e revolucione o atendimento da sua empresa. Sem cartão de crédito.
            </Typography>

            <Formik
              initialValues={user}
              enableReinitialize={true}
              validationSchema={UserSchema}
              onSubmit={(values, actions) => {
                setTimeout(() => {
                  handleSignUp(values);
                  actions.setSubmitting(false);
                }, 400);
              }}
            >
              {({ touched, errors, isSubmitting }) => (
                <Form className={classes.formContainer}>
                  <Field
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    id="companyName"
                    placeholder={i18n.t("signup.form.company")}
                    error={touched.companyName && Boolean(errors.companyName)}
                    helperText={touched.companyName && errors.companyName}
                    name="companyName"
                    autoComplete="companyName"
                    className={classes.inputRounded}
                    InputProps={{ startAdornment: null }}
                  />

                  <Field
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    id="name"
                    placeholder={i18n.t("signup.form.name")}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    name="name"
                    autoComplete="name"
                    className={classes.inputRounded}
                    InputProps={{ startAdornment: null }}
                  />

                  <Field
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    id="phone"
                    placeholder={i18n.t("signup.form.phone")}
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    name="phone"
                    autoComplete="phone"
                    className={classes.inputRounded}
                    InputProps={{ startAdornment: null }}
                  />

                  <Field
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    id="email"
                    placeholder={i18n.t("signup.form.email")}
                    name="email"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    autoComplete="email"
                    className={classes.inputRounded}
                    InputProps={{ startAdornment: null }}
                  />

                  <Field
                    as={TextField}
                    variant="outlined"
                    fullWidth
                    name="password"
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    placeholder={i18n.t("signup.form.password")}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    className={classes.inputRounded}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge='end'>
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <FormControl fullWidth variant="outlined" className={classes.inputRounded}>
                    <InputLabel id="plan-selection-label">Plano</InputLabel>
                    <Field
                      as={Select}
                      labelId="plan-selection-label"
                      id="plan-selection"
                      name="planId"
                      label="Plano"
                      required
                    >
                      {plans.map((plan, key) => (
                        <MenuItem key={key} value={plan.id}>
                          {plan.name} - R$ {plan.amount}
                        </MenuItem>
                      ))}
                    </Field>
                  </FormControl>

                  {/* Checkbox Termos de Uso */}
                  <FormControlLabel
                    control={
                      <Field
                        as={Checkbox}
                        name="acceptTerms"
                        color="primary"
                      />
                    }
                    label={
                      <span style={{ fontSize: '0.9rem', color: '#1c1e21' }}>
                        Eu aceito os <RouterLink to="/termos" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 'bold' }}>Termos de Serviço</RouterLink> e <RouterLink to="/privacidade" style={{ color: theme.palette.primary.main, textDecoration: 'none', fontWeight: 'bold' }}>Política de Privacidade</RouterLink>
                      </span>
                    }
                    style={{ marginTop: '10px' }}
                  />
                  {touched.acceptTerms && errors.acceptTerms && (
                    <Typography variant="caption" color="error" style={{ marginLeft: '14px' }}>
                      {errors.acceptTerms}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    className={classes.submitButton}
                  >
                    {i18n.t("signup.buttons.submit")}
                  </Button>

                  <div className={classes.loginRow}>
                    Já possui uma conta?{' '}
                    <RouterLink to="/login" style={{ textDecoration: 'none', fontWeight: 'bold' }}>
                      <span style={{ color: theme.palette.primary.main }}>Entrar</span>
                    </RouterLink>
                  </div>
                </Form>
              )}
            </Formik>

          </Grid>

          <Grid item xs={12} md={6} className={classes.rightCol}>
            <img
              src='https://minio3.sourcex.dev/typebot/public/workspaces/cmdkn2fsw0000pe17tv37l3vj/typebots/cmjh5wm5t0008t41aw8hxafcm/blocks/lqi7ikensf2lsxxbfxa8xgnf?v=1769034746048'
              alt='Conexão e Conversas'
              className={classes.mockupImg}
            />
          </Grid>

        </Grid>
      </Container>


      <footer className={classes.footer}>
        <span>© 2026 ZapBot</span>
        <RouterLink to="/privacidade" style={{ color: 'inherit', textDecoration: 'none' }}>Privacidade</RouterLink>
        <RouterLink to="/termos" style={{ color: 'inherit', textDecoration: 'none' }}>Termos</RouterLink>
      </footer>
    </div>
  );
};

export default SignUp;
